import { StudyStatus } from '@eva-pacs/client';
import { useStudyActions } from '../study/useStudyActions';
import { UpdateEvent } from './useWorklistUpdates';

export interface ProcessUpdateWatcherProps {
  studyPractitionerId: string;
  eventType: UpdateEvent;
  studyOldPractitionerId: string;
  userId: string;
  status?: string;
  oldStatus: string;
}

const getIsIncomingWorklist = (studyOldPractitionerId: string, practitionerId: string, studyPractitionerId) => {
  return studyOldPractitionerId !== practitionerId && studyPractitionerId === practitionerId;
};

const getIsOutComingWorklist = (sessionPractitionerId: string, studyPractitionerId, studyOldPractitionerId: string) => {
  return sessionPractitionerId !== studyPractitionerId && studyOldPractitionerId === sessionPractitionerId;
};

const isSamePractitionerUpdate = (
  studyOldPractitionerId: string,
  studyPractitionerId: string,
  sessionPractitionerId: string,
) => {
  return studyOldPractitionerId === studyPractitionerId && studyPractitionerId === sessionPractitionerId;
};

export const useWorklistCounterWatcher = () => {
  const { incrementWorklistTotalCount, decrementWorklistTotalCount } = useStudyActions();
  const processUpdateCounter = ({
    studyPractitionerId,
    eventType,
    studyOldPractitionerId,
    userId,
    status,
    oldStatus,
  }: ProcessUpdateWatcherProps) => {
    switch (eventType) {
      case UpdateEvent.DELETED: {
        // Only decrement counter if the study is assigned to the current practitioner in session.
        if (studyOldPractitionerId === userId) {
          return decrementWorklistTotalCount();
        }
        return;
      }

      case UpdateEvent.UPDATED: {
        const isCurrentWorklistStudy = studyPractitionerId === userId;

        const isOutComingWorklist = getIsOutComingWorklist(userId, studyPractitionerId, studyOldPractitionerId);
        const isIncomingWorklist = getIsIncomingWorklist(studyOldPractitionerId, userId, studyPractitionerId);

        // increment only when there are new items added to worklist
        if (isIncomingWorklist) {
          if (
            status === StudyStatus.IMAGES_SENT ||
            status === StudyStatus.READING ||
            status === StudyStatus.READING_PENDING
          ) {
            return incrementWorklistTotalCount();
          }
        }

        if (isSamePractitionerUpdate(studyOldPractitionerId, studyPractitionerId, userId)) {
          if (
            oldStatus === StudyStatus.SIGNED &&
            (status === StudyStatus.READING ||
              status === StudyStatus.IMAGES_SENT ||
              status === StudyStatus.READING_PENDING)
          )
            return incrementWorklistTotalCount();
        }

        // this cases are never in worklist, so don't do anything
        if (
          oldStatus === StudyStatus.ADDENDUM_SIGNED ||
          oldStatus === StudyStatus.IN_ADDENDUM ||
          oldStatus === StudyStatus.DELIVERED
        )
          return;

        // Unassign case.
        if (isOutComingWorklist && oldStatus !== StudyStatus.SIGNED) {
          if (!studyPractitionerId && studyOldPractitionerId !== studyPractitionerId)
            return decrementWorklistTotalCount();
          return decrementWorklistTotalCount();
        }

        if (isCurrentWorklistStudy && !isIncomingWorklist) {
          // When signing a study which is a worklist study, decrement it.
          if (status === StudyStatus.SIGNED) return decrementWorklistTotalCount();
        }

        return;
      }
      default:
        return '';
    }
  };

  return {
    processUpdateCounter,
  };
};
